<template>
  <Teleport to="body">
    <Transition name="modal">
      <div v-if="show" class="fixed inset-0 z-[100] flex items-center justify-center p-4">
        <div class="absolute inset-0 bg-black/50 backdrop-blur-sm" />
        <div class="relative bg-white rounded-2xl shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col animate-fade-in">
          <!-- Header -->
          <div class="flex items-center justify-between px-6 pt-5 pb-3 shrink-0">
            <div class="flex items-center gap-3 min-w-0">
              <div class="min-w-0">
                <h3 class="text-lg font-semibold text-gray-900 truncate">{{ title }}</h3>
                <p v-if="subtitle" class="text-sm text-gray-400 mt-0.5 truncate">{{ subtitle }}</p>
              </div>
              <span v-if="live" class="badge-live shrink-0">Live</span>
            </div>
            <button @click="$emit('close')" class="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 shrink-0 ml-4">
              <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </button>
          </div>

          <!-- Player -->
          <div class="mx-6 bg-black rounded-xl overflow-hidden h-[350px] relative">
            <VideoPlayer
              v-if="activeUrl"
              ref="player"
              :src="activeUrl"
              :type="activeType"
              :live="live"
              class="h-full"
              @playing="isPlaying = true"
              @paused="isPlaying = false"
            />
            <div v-else class="flex flex-col items-center justify-center h-full text-center">
              <svg class="w-10 h-10 text-gray-600 mb-2" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1" stroke-linecap="round" stroke-linejoin="round">
                <polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2" ry="2"/>
              </svg>
              <p class="text-sm text-gray-500">Select a format to play</p>
            </div>
          </div>

          <!-- Format list -->
          <div class="flex-1 overflow-y-auto mt-3">
            <!-- HLS (only if url.hls exists and no original_abr format) -->
            <div v-if="showHlsRow" class="mx-4">
              <button
                @click="selectFormat('hls', hlsUrl, 'application/x-mpegURL')"
                :class="['w-full flex items-center justify-between px-4 py-3 rounded-lg transition-colors', activeId === 'hls' ? 'bg-[var(--color-primary-light)]' : 'hover:bg-gray-50']"
              >
                <div class="flex items-center gap-3 min-w-0">
                  <div :class="['w-8 h-8 rounded-lg flex items-center justify-center shrink-0', activeId === 'hls' ? 'bg-[var(--color-primary-glow)]' : 'bg-green-50']">
                    <svg class="w-3.5 h-3.5" :class="activeId === 'hls' ? 'text-[var(--color-primary)]' : 'text-green-500'" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="4"/><path d="M16.24 7.76a6 6 0 010 8.49m-8.48-8.49a6 6 0 000 8.49" stroke-linecap="round"/></svg>
                  </div>
                  <span class="text-sm font-medium" :class="activeId === 'hls' ? 'text-[var(--color-primary)]' : 'text-gray-900'">HLS Stream</span>
                </div>
                <div :class="['p-2 rounded-lg transition-colors', activeId === 'hls' ? 'text-[var(--color-primary)]' : 'text-gray-400']">
                  <svg v-if="activeId === 'hls' && isPlaying" class="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="4" width="4" height="16" rx="1"/><rect x="14" y="4" width="4" height="16" rx="1"/></svg>
                  <svg v-else class="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>
                </div>
              </button>
            </div>

            <!-- Exported formats -->
            <div v-for="fmt in formats" :key="fmt.id" class="mx-4">
              <div :class="['flex items-center justify-between px-4 py-3 rounded-lg transition-colors', fmt.status === 'failed' ? 'bg-red-50' : activeId === fmt.id ? 'bg-[var(--color-primary-light)]' : 'hover:bg-gray-50']">
                <div class="flex items-center gap-3 min-w-0 flex-1">
                  <div :class="['w-8 h-8 rounded-lg flex items-center justify-center shrink-0', fmt.status === 'failed' ? 'bg-red-100' : activeId === fmt.id ? 'bg-[var(--color-primary-glow)]' : 'bg-violet-50']">
                    <svg v-if="fmt.status === 'failed'" class="w-3.5 h-3.5 text-red-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>
                    <svg v-else class="w-3.5 h-3.5" :class="activeId === fmt.id ? 'text-[var(--color-primary)]' : 'text-violet-500'" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2" ry="2"/></svg>
                  </div>
                  <div class="min-w-0">
                    <p class="text-sm font-medium" :class="fmt.status === 'failed' ? 'text-red-600' : activeId === fmt.id ? 'text-[var(--color-primary)]' : 'text-gray-900'">{{ fmt.id }}</p>
                    <p v-if="fmt.status === 'failed'" class="text-sm text-red-400">Transcoding failed</p>
                    <p v-else-if="fmt.fileSize" class="text-xs font-data text-gray-400">{{ formatBytes(fmt.fileSize) }}</p>
                  </div>
                </div>
                <div v-if="fmt.status !== 'failed'" class="flex items-center gap-1 shrink-0">
                  <button
                    @click="selectFormat(fmt.id, fmt.remoteUrl, detectType(fmt.remoteUrl, fmt.id))"
                    :class="['p-2 rounded-lg transition-colors', activeId === fmt.id ? 'text-[var(--color-primary)] bg-[var(--color-primary-glow)]' : 'text-gray-400 hover:text-green-500 hover:bg-green-50']"
                    :title="activeId === fmt.id && isPlaying ? 'Pause' : 'Play'"
                  >
                    <svg v-if="activeId === fmt.id && isPlaying" class="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="4" width="4" height="16" rx="1"/><rect x="14" y="4" width="4" height="16" rx="1"/></svg>
                    <svg v-else class="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>
                  </button>
                  <a v-if="fmt.remoteUrl && !fmt.remoteUrl.endsWith('.m3u8') && fmt.id !== 'original_abr'" :href="fmt.remoteUrl" target="_blank" download @click.stop class="p-2 rounded-lg text-gray-400 hover:text-amber-500 hover:bg-amber-50 transition-colors" title="Download">
                    <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
                    </svg>
                  </a>
                  <button @click="copyUrl(fmt.remoteUrl)" v-if="fmt.remoteUrl" class="p-2 rounded-lg text-gray-400 hover:text-[var(--color-primary)] hover:bg-[var(--color-primary-light)] transition-colors" title="Copy URL">
                    <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/>
                    </svg>
                  </button>
                </div>
                <span v-else class="badge-error shrink-0">Failed</span>
              </div>
            </div>

            <div v-if="!formats.length && !showHlsRow" class="px-6 py-8 text-center">
              <p class="text-sm text-gray-400">No formats available</p>
            </div>
          </div>

          <div class="h-4 shrink-0" />
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script>
import VideoPlayer from './VideoPlayer.vue'

export default {
  name: 'MediaPlayerModal',
  components: { VideoPlayer },
  props: {
    show: { type: Boolean, default: false },
    title: { type: String, default: '' },
    subtitle: { type: String, default: '' },
    hlsUrl: { type: String, default: '' },
    formats: { type: Array, default: () => [] },
    live: { type: Boolean, default: false }
  },
  emits: ['close'],

  computed: {
    hasOriginalAbr() {
      return this.formats.some(f => f.id === 'original_abr')
    },
    showHlsRow() {
      return !!this.hlsUrl && !this.hasOriginalAbr
    }
  },

  data() {
    return {
      activeId: null,
      activeUrl: '',
      activeType: '',
      isPlaying: false
    }
  },

  watch: {
    show(val) {
      // Reset state when closing so reopening with a new URL works
      if (!val) {
        this.activeId = null
        this.activeUrl = ''
        this.activeType = ''
        this.isPlaying = false
        return
      }
      // Auto-select first playable format
      const abr = this.formats.find(f => f.id === 'original_abr')
      if (abr && abr.remoteUrl) {
        this.selectFormat(abr.id, abr.remoteUrl, this.detectType(abr.remoteUrl, abr.id))
      } else if (this.hlsUrl) {
        this.selectFormat('hls', this.hlsUrl, 'application/x-mpegURL')
      } else if (this.formats.length) {
        const first = this.formats.find(f => f.remoteUrl) || this.formats[0]
        if (first?.remoteUrl) this.selectFormat(first.id, first.remoteUrl, this.detectType(first.remoteUrl, first.id))
      }
    }
  },

  methods: {
    selectFormat(id, url, type) {
      if (this.activeId === id) {
        this.$refs.player?.togglePlay()
      } else {
        this.activeId = id
        this.activeUrl = url
        this.activeType = type
      }
    },

    detectType(url, id) {
      if (id === 'mp3') return 'audio/mpeg'
      if (id === 'hls' || id === 'original_abr') return 'application/x-mpegURL'
      if (!url) return 'video/mp4'
      if (url.endsWith('.m3u8')) return 'application/x-mpegURL'
      if (url.endsWith('.mp3')) return 'audio/mpeg'
      return 'video/mp4'
    },

    async copyUrl(url) {
      if (url) {
        await navigator.clipboard.writeText(url)
      }
    },

    formatBytes(bytes) {
      if (!bytes) return ''
      const units = ['B', 'KB', 'MB', 'GB']
      let size = bytes, i = 0
      while (size >= 1024 && i < units.length - 1) { size /= 1024; i++ }
      return size.toFixed(i > 0 ? 1 : 0) + ' ' + units[i]
    }
  }
}
</script>

<style scoped>
.modal-enter-active, .modal-leave-active { transition: opacity 0.2s ease; }
.modal-enter-from, .modal-leave-to { opacity: 0; }
</style>
